import {
  createFileRoute,
  Outlet,
  redirect,
  useRouter
} from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { parse } from 'valibot'

import { PageLayout } from '@/components/layouts'
import { LABELS } from '@/constants/common/message.constants'
import { getDefaultUserMeTenant } from '@/features/auth/models'
import { usePageNavigation, useSortNavigation } from '@/features/shared/hooks'
import { fetchShipmentOrders } from '@/features/shipment-orders/api'
import {
  isSameSummaryQuery,
  useInvalidateShipmentOrdersSummary,
  useRefreshShipmentOrders,
  useShipmentOrdersSummaryQuery
} from '@/features/shipment-orders/hooks'
import { USER_TYPE } from '@/libs/api/types'

import { selectedDcIdAtom, tenantStore } from '../-states'

import {
  ShipmentOrdersHeader,
  ShipmentOrdersSearchForm,
  ShipmentOrdersSearchSummary,
  ShipmentOrdersTable
} from './-components'
import {
  getDefaultSearch,
  queryFrom,
  resolveEffectiveSearch,
  shipmentOrdersSearchSchema,
  type ShipmentOrdersSearch
} from './-models'

export const Route = createFileRoute('/_protected/$tenantKey/shipment-orders')({
  meta: () => [{ title: LABELS.SHIPMENT_ORDER }],
  validateSearch: (search) => parse(shipmentOrdersSearchSchema, search),
  loaderDeps: ({ search }) => search,
  beforeLoad: ({ search, context: { auth } }) => {
    if (auth.user.type !== USER_TYPE.OWNER) {
      throw redirect({
        to: `/${getDefaultUserMeTenant(auth.user).key}`
      })
    }
    const selectedDcId = tenantStore.get(selectedDcIdAtom)
    if (search.dcId != selectedDcId) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          dcId: selectedDcId
        }
      })
    }
  },
  loader: async ({
    abortController,
    params: { tenantKey },
    deps,
    context: { auth }
  }) => {
    // 初回レンダリング時の初期値
    const initialSearch = resolveEffectiveSearch(deps)

    const [shipmentOrders] = await Promise.all([
      fetchShipmentOrders(
        { tenantKey, query: queryFrom(initialSearch) },
        abortController.signal
      )
    ])
    return {
      shipmentOrders,
      authUser: auth.user,
      initialSearch
    }
  },
  component: ShipmentOrdersPage,
  // 存在しないパスの場合にOutletで404ページが表示されてしまうのでnullを返す
  notFoundComponent: () => null
})

function ShipmentOrdersPage() {
  const {
    authUser,
    shipmentOrders: { data, pagination },
    initialSearch
  } = Route.useLoaderData()

  const search = Route.useSearch()
  const { tenantKey } = Route.useParams()
  const navigate = Route.useNavigate()

  // 検索結果全体の集計（#5285 2.1）。一覧と同じ条件（loader が解決した initialSearch）で取る
  const summaryQuery = useShipmentOrdersSummaryQuery({
    tenantKey,
    query: queryFrom(initialSearch)
  })

  const handleDataRefresh = useRefreshShipmentOrders(tenantKey)
  const invalidateSummary = useInvalidateShipmentOrdersSummary(tenantKey)

  // 別の画面から一覧に入ったら、検索結果サマリーを取り直す。
  // 配車板・運行実績・CSV 取込など一覧の外での書き換えは useRefreshShipmentOrders を通らず、
  // サマリーは staleTime 5 分で持つので、戻ってきたときに一覧（loader が毎回取り直す）とずれる。
  // 一覧はページ・並び・検索条件が変わるたびに再マウントされるので、マウントだけでは区別できない。
  // 描画の時点の resolvedLocation は遷移元を指す（router が描画のあとで現在地に更新する）ので、
  // パスが違えば別の画面から入ってきたと判定する
  const router = useRouter()
  const [isEnteredFromOtherPage] = useState(
    () =>
      router.state.resolvedLocation.pathname !== router.state.location.pathname
  )
  useEffect(() => {
    if (isEnteredFromOtherPage) void invalidateSummary()
  }, [isEnteredFromOtherPage, invalidateSummary])

  const handleSubmit = useCallback(
    (nextSearch: ShipmentOrdersSearch) => {
      navigate({ search: nextSearch })
      // 集計の条件が変わらない再検索では URL もキーも変わらず、一覧だけ loader で取り直される。
      // サマリーも同じタイミングで取り直す。条件が変われば新しいキーで取るので、
      // ここで invalidate すると古い条件の集計を 1 回余分に走らせてしまう
      if (
        isSameSummaryQuery(
          tenantKey,
          queryFrom(nextSearch),
          queryFrom(initialSearch)
        )
      ) {
        void invalidateSummary()
      }
    },
    [navigate, invalidateSummary, tenantKey, initialSearch]
  )

  const handlePageChange = usePageNavigation(search, navigate)

  const handleSortChange = useSortNavigation(search, navigate)

  const selectedDcId = useAtomValue(selectedDcIdAtom)
  const currentDcId = useRef(selectedDcId)

  useEffect(() => {
    if (currentDcId.current !== selectedDcId) {
      currentDcId.current = selectedDcId
      navigate({
        search: {
          dcId: selectedDcId
        }
      })
    }
  }, [search, navigate, selectedDcId])

  return (
    <>
      <PageLayout>
        <div className="flex flex-col gap-2" data-testid="shipment-orders-page">
          <ShipmentOrdersHeader tenantKey={tenantKey} search={initialSearch} />
          <ShipmentOrdersSearchForm
            authUser={authUser}
            tenantKey={tenantKey}
            search={initialSearch}
            defaultSearch={getDefaultSearch()}
            onSubmit={handleSubmit}
            preservedTake={pagination?.take}
          />
          <ShipmentOrdersTable
            tenantKey={tenantKey}
            authUser={authUser}
            shipmentOrders={data}
            pagination={pagination}
            search={search}
            onChangePage={handlePageChange}
            onChangeSort={handleSortChange}
            onDataRefresh={handleDataRefresh}
            summary={
              <ShipmentOrdersSearchSummary
                data={summaryQuery.data}
                isLoading={summaryQuery.isLoading}
                isFetching={summaryQuery.isFetching}
                isError={summaryQuery.isError}
              />
            }
          />
        </div>
      </PageLayout>
      <Outlet />
    </>
  )
}
